import { observer } from 'mobx-react-lite'
import { TableRow } from '@/components/Table'
import { type ScopeChangeRequestDetailsViewModel } from '@/features/admin/scope-change-requests/components/ScopeChangeRequestDetailsPanel/ScopeChangeRequestDetailsViewModel'
import { ScopeChangePriceBreakDownItemForAdmin } from '@/features/admin/scope-change-requests/ScopeChangeRequest'
import { QuantityCell } from '@/features/admin/scope-change-requests/components/ScopeChangeRequestDetailsPanel/ItemsSection/components/QuantityCell'
import { ServiceCell } from '@/features/admin/scope-change-requests/components/ScopeChangeRequestDetailsPanel/ItemsSection/components/ServiceCell'
import { MoneyCellContainer } from '@/features/admin/scope-change-requests/components/ScopeChangeRequestDetailsPanel/ItemsSection/components/MoneyCell/MoneyCellContainer'
import { type CostPerCustomServiceViewModel } from '@/features/admin/scope-change-requests/components/ScopeChangeRequestDetailsPanel/CostPerCustomServiceViewModel'
import { ItemsBreakdownSection } from '@/features/admin/scope-change-requests/components/ScopeChangeRequestDetailsPanel/ItemsSection/ItemsBreakdownSection'

export interface CustomItemsSectionContainerProps {
  vm: ScopeChangeRequestDetailsViewModel
}

export const CustomItemsSectionContainer = observer(
  function CustomItemsSectionContainer({
    vm,
  }: CustomItemsSectionContainerProps) {
    return (
      <ItemsBreakdownSection
        title="Custom services"
        itemColumnTitle="Service"
        isExpanded={vm.customItemsExpanded}
        onToggleExpanded={vm.toggleCustomItemsSection}
        count={
          vm.scopeChangeRequestPriceBreakDown.customScopeChangeItems.length
        }
        showEditButton={vm.canEditItems}
        onEdit={vm.editCustomItems}
      >
        {vm.customItemRows.map((item, i) => (
          <CustomItemRowContainer
            editable={vm.canEditItems}
            item={item}
            key={
              item instanceof ScopeChangePriceBreakDownItemForAdmin
                ? `${item.name}-${i}`
                : item.fromItem.id
            }
          />
        ))}
      </ItemsBreakdownSection>
    )
  },
)

interface CustomItemRowContainerProps {
  item: CostPerCustomServiceViewModel | ScopeChangePriceBreakDownItemForAdmin
  editable: boolean
}

const CustomItemRowContainer = observer(function CustomItemRowContainer({
  item,
  editable,
}: CustomItemRowContainerProps) {
  if (item instanceof ScopeChangePriceBreakDownItemForAdmin) {
    return (
      <TableRow>
        <QuantityCell>{item.quantityDiff}</QuantityCell>
        <ServiceCell>{item.name}</ServiceCell>
        <MoneyCellContainer
          editable={editable}
          model={item}
          testId="scope-change-request-custom-item-price"
          field="priceDiff"
        >
          {item.priceDiff}
        </MoneyCellContainer>
        <MoneyCellContainer
          editable={editable}
          model={item}
          testId="scope-change-request-custom-item-pro-price"
          field="proPriceDiff"
        >
          {item.proPriceDiff}
        </MoneyCellContainer>
      </TableRow>
    )
  }

  return (
    <TableRow>
      <QuantityCell>{item.fromItem.quantity}</QuantityCell>
      <ServiceCell>{item.fromItem.name}</ServiceCell>
      <MoneyCellContainer
        editable={editable}
        model={item}
        testId="scope-change-request-item-price"
        field="newPrice"
        error={item.validation.getError('newPrice')}
      >
        {item.fromItem.price}
      </MoneyCellContainer>
      <MoneyCellContainer
        editable={editable}
        model={item}
        testId="scope-change-request-item-pro-price"
        field="newProPrice"
        error={item.validation.getError('newProPrice')}
      >
        {item.fromItem.proPrice}
      </MoneyCellContainer>
    </TableRow>
  )
})
